import React from "react";
import { Skeleton, SkeletonSwap } from "./Skeleton";
import TiltCard from "./TiltCard";
import { motion, useReducedMotion } from "motion/react";
import { spring } from "../lib/springs";

/* Tabler's trending-up / trending-down, inlined.
   Font Awesome 4 (the app's only icon font) has no diagonal-arrow pair, and
   these two glyphs are the whole visual grammar of the tile: the pill and the
   footer line both read "which way is this going". */
const TrendGlyph = ({ dir }) => {
  if (dir !== "up" && dir !== "down") return null;
  return (
    <svg
      className="stat-card-trend"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
    >
      {dir === "up" ? (
        <>
          <polyline points="3 17 9 11 13 15 21 7" />
          <polyline points="14 7 21 7 21 14" />
        </>
      ) : (
        <>
          <polyline points="3 7 9 13 13 9 21 17" />
          <polyline points="21 10 21 17 14 17" />
        </>
      )}
    </svg>
  );
};

/**
 * Dashboard stat tile.
 *
 *   label      muted description, top-left
 *   value      the figure — rendered tabular so a column of tiles aligns
 *   figures    `[{ label, value, sub, tone }]` — two or three figures side by
 *              side in place of `value`, for a tile that answers more than
 *              one count (leave taken / pending). `tone: "attention"` colours
 *              a non-zero figure
 *   href       when set, `value` becomes an external link instead of a figure
 *   badge      pill text, top-right (omitted when falsy)
 *   badgeTrend "up" | "down" — arrow inside the pill; omit when the number is
 *              not a comparison, because the arrow claims one
 *   badgeTone  "success" | "danger" | "attention" — default is the outline pill
 *   breakdown  `[{ label, value, sub }]` — what the figure is made of, listed
 *              under it. A part with nothing in it still gets a row: a zero
 *              says "none of these", where a missing row says nothing at all
 *   note       footer line, in text colour, with optional `noteTrend` arrow
 *   sub        footer line, muted
 *   action     `{ label, onClick, expanded, controls, id }` — a quiet text
 *              button at the foot of the tile, for a tile whose figures have
 *              detail behind them. `expanded` (with the id of the region in
 *              `controls`) makes it a disclosure: aria-expanded and a chevron
 *              that turns over. `id` lets that region hand focus back
 *   loading    the figure is not known yet — draw placeholder bars in the
 *              value and footer instead of a stand-in zero
 *
 * Every tile carries the same accent wash — there is no per-tile hue prop;
 * status lives in the badge. Styling is the `.stat-card` block of
 * assets/css/theme.css.
 *
 * A settled tile is a <TiltCard tilt={false}/>: a glare follows the pointer
 * but the tile itself does not move. A loading tile has no glare — there is nothing on it worth inspecting yet.
 */
const StatCard = ({
  label,
  value,
  figures,
  href,
  badge,
  badgeTrend,
  badgeTone,
  breakdown,
  note,
  noteTrend,
  sub,
  action,
  loading = false,
}) => {
  const hasFigures = Array.isArray(figures) && figures.length > 0;
  const reduce = useReducedMotion() ?? false;

  /* Before the first response the figure is not "0" - it is not known yet.
     Rendering the fallback zero made every tile count down from 0 to its real
     value a beat later, which reads as data changing rather than data
     arriving. A bar in the figure's own box says "not yet" and keeps the tile
     exactly the height it will be.
     The card itself is one shell for both states; only what is inside it
     crossfades (SkeletonSwap) when the figure lands. */
  /* The bars are sized in `em`, so each one inherits the line box of the
     text it stands in for (30px/1.1 for the figure, 13.5px/1.4 in the
     footer): bar height plus its margins add up to exactly one line, and
     the tile is already its final height. */
  const skeleton = (
      <>
        <div className="stat-card-head">
          <span className="stat-card-label">{label}</span>
          {hasFigures ? (
            <div className="stat-card-figures">
              {figures.map((figure, index) => (
                <div className="stat-card-figure" key={figure.label ?? index}>
                  <span className="stat-card-figure-label">{figure.label}</span>
                  <span className="stat-card-value">
                    <Skeleton
                      width="2.5ch"
                      height=".72em"
                      style={{ margin: ".19em 0" }}
                    />
                  </span>
                  {figure.sub !== undefined ? (
                    <Skeleton width="6ch" height=".8em" />
                  ) : null}
                </div>
              ))}
            </div>
          ) : (
            <div className="stat-card-value">
              <Skeleton
                width="3.5ch"
                height=".72em"
                style={{ margin: ".19em 0" }}
              />
            </div>
          )}
        </div>
        {breakdown && breakdown.length ? (
          <div className="stat-card-breakdown">
            {breakdown.map((part, index) => (
              <div className="stat-card-breakdown-row" key={part.label ?? index}>
                <Skeleton width="7ch" height=".8em" />
                <Skeleton width="5ch" height=".8em" />
              </div>
            ))}
          </div>
        ) : null}
        {note || sub || action ? (
          <div className="stat-card-foot">
            {note ? (
              <Skeleton
                width="72%"
                height=".72em"
                style={{ margin: ".34em 0" }}
              />
            ) : null}
            {sub ? (
              <Skeleton
                width="48%"
                height=".72em"
                style={{ margin: ".34em 0" }}
              />
            ) : null}
            {action ? (
              <Skeleton
                width="9ch"
                height=".72em"
                style={{ margin: ".34em 0" }}
              />
            ) : null}
          </div>
        ) : null}
      </>
  );

  // built only once the figure is known: it reads values a loading tile lacks
  const renderContent = () => (
    <>
      <div className="stat-card-head">
        <span className="stat-card-label">{label}</span>
        {badge ? (
          <span
            className={
              "stat-card-badge" + (badgeTone ? " is-" + badgeTone : "")
            }
          >
            <TrendGlyph dir={badgeTrend} />
            {badge}
          </span>
        ) : null}
        {hasFigures ? (
          <div className="stat-card-figures">
            {figures.map((figure, index) => (
              <div
                className={
                  "stat-card-figure" +
                  (figure.tone && Number(figure.value) > 0
                    ? " is-" + figure.tone
                    : "")
                }
                key={figure.label ?? index}
              >
                <span className="stat-card-figure-label">{figure.label}</span>
                <span className="stat-card-value">{figure.value}</span>
                {figure.sub ? (
                  <span className="stat-card-figure-sub">{figure.sub}</span>
                ) : null}
              </div>
            ))}
          </div>
        ) : (
          <div
            className={"stat-card-value" + (href ? " stat-card-value-link" : "")}
          >
            {href ? (
              <a href={href} target="_blank" rel="noopener noreferrer">
                {value}
              </a>
            ) : (
              value
            )}
          </div>
        )}
      </div>
      {breakdown && breakdown.length ? (
        /* A description list, not a table: three parts of one figure is a
           set of term/value pairs, and a <table> here would announce a grid
           with no header to a screen reader. */
        <dl className="stat-card-breakdown">
          {breakdown.map((part, index) => (
            <div className="stat-card-breakdown-row" key={part.label ?? index}>
              <dt className="stat-card-breakdown-label">{part.label}</dt>
              <dd className="stat-card-breakdown-figure">
                <span className="stat-card-breakdown-value">{part.value}</span>
                {part.sub ? (
                  <span className="stat-card-breakdown-sub">{part.sub}</span>
                ) : null}
              </dd>
            </div>
          ))}
        </dl>
      ) : null}
      {note || sub || action ? (
        <div className="stat-card-foot">
          {note ? (
            <div className="stat-card-note">
              {note}
              <TrendGlyph dir={noteTrend} />
            </div>
          ) : null}
          {sub ? <div className="stat-card-sub">{sub}</div> : null}
          {action ? (
            <button
              type="button"
              id={action.id}
              className="stat-card-action"
              onClick={action.onClick}
              aria-expanded={
                action.expanded === undefined ? undefined : Boolean(action.expanded)
              }
              aria-controls={action.controls}
            >
              {action.label}
              {action.expanded === undefined ? (
                <i className="fa fa-arrow-right" aria-hidden="true"></i>
              ) : (
                /* one chevron that turns, on the same spring as the region
                   it opens (components/Collapsible.js) */
                <motion.span
                  className="stat-card-indicator"
                  aria-hidden="true"
                  initial={false}
                  animate={{ rotate: action.expanded ? 180 : 0 }}
                  transition={reduce ? { duration: 0 } : spring.slow}
                >
                  <i className="fa fa-chevron-down"></i>
                </motion.span>
              )}
            </button>
          ) : null}
        </div>
      ) : null}
    </>
  );

  return (
    <TiltCard
      tilt={false}
      glare={!loading}
      className="card stat-card"
      aria-busy={loading ? "true" : undefined}
    >
      <SkeletonSwap loaded={!loading} skeleton={skeleton} className="stat-card-body">
        {loading ? null : renderContent()}
      </SkeletonSwap>
    </TiltCard>
  );
};

export default StatCard;
